import { CordovaPlugins } from '../../../shared/CordovaPlugins';
import { isIos, isNative } from '../../../shared/utils/is';
import { confirmNotification, showErrorMessage } from '../../../shared/utils/notifications';
import { loadScript } from '../loadScript';
import {
    SUPPORT_CHAT_WIDGET_KEY,
    SUPPORT_CHAT_ZENDESK_VALUE,
    getLocalStorageWithExpiry,
    setLocalStorageWithExpiry
} from './supportChatUtils';

const fetchZendeskJwt = async () =>
    new Promise((resolve) => {
        $f.mobile.ajax({
            url: '/api/v3/users/current/zendesk_auth/',
            success: (response) => {
                resolve(response.data.token);
            },
            error: () => {
                // if we send `null`, chat widget tries to load endlessly
                resolve('null');
            }
        });
    });

export const loadZendesk = async () => {
    await loadScript(
        `https://static.zdassets.com/ekr/snippet.js?key=${process.env.ZENDESK_MESSENGER_CRUNCHTIME_WIDGET_KEY}`,
        'ze-snippet'
    );
};

export const initZendesk = async () => {
    try {
        await loadZendesk();
    } catch (e) {
        return;
    }

    // window.zE is Zendesk's widget API, created by loadZendesk()
    if (!window.zE) {
        return;
    }

    window.zE('messenger:on', 'unreadMessages', function (count) {
        const widget = getLocalStorageWithExpiry(SUPPORT_CHAT_WIDGET_KEY);
        if (count === 0 && widget !== SUPPORT_CHAT_ZENDESK_VALUE) {
            localStorage.setItem(SUPPORT_CHAT_WIDGET_KEY, JSON.stringify({ value: 'forethought' }));
        } else {
            setLocalStorageWithExpiry(
                SUPPORT_CHAT_WIDGET_KEY,
                SUPPORT_CHAT_ZENDESK_VALUE,
                parseInt(process.env.ZENDESK_TIMEOUT)
            );
        }
    });

    window.zE(() => {
        window.zE('messenger', 'loginUser', (callback) => {
            fetchZendeskJwt().then((token) => {
                callback(token);
            });
        });

        if (isIos() && isNative()) {
            // On iOS, we lock the window scroll because everything scrolls inside <div class="page_scrollable">. However,
            // the Zendesk Widget needs the window to scroll so the keyboard doesn't cover the text input, etc. We will
            // temporarily unlock the window scroll until the widget is closed.
            window.zE('messenger:on', 'open', () => {
                CordovaPlugins().Keyboard.disableScroll(false);
            });

            window.zE('messenger:on', 'close', () => {
                CordovaPlugins().Keyboard.disableScroll(true);
            });
        }

        window.zE('messenger:on', 'close', () => {
            confirmNotification(
                _t('mobile_common_chat_ending_dialog_description'),
                (confirm) => {
                    if (confirm) {
                        localStorage.setItem('support_chat_current_widget', JSON.stringify({ value: 'forethought' }));
                        window.Forethought('widget', 'clearLocalData');
                    }
                },
                _t('mobile_common_chat_ending_dialog_title'),
                _t('mobile_common_chat_ending_dialog_confirm'),
                _t('mobile_common_chat_ending_dialog_cancel')
            );
        });
    });
};

const activateWidgetAfterLoad = () =>
    new Promise((resolve) => {
        window.zE(() => {
            window.zE('messenger', 'show');
            window.zE('messenger', 'open');
            resolve();
        });
    });

export const showZendeskWidget = async () => {
    if (!window.zE) {
        showErrorMessage(_t('mobile_common_chat_unavailable'));
        return;
    }

    await activateWidgetAfterLoad();
};
